import React from 'react';
import { CANONICAL_ORIGIN, getAbsoluteUrl, HREFLANG } from '../config/routeMap';

/**
 * <JsonLd type="…" language="en|fr" data={…} />
 *
 * Emits a <script type="application/ld+json"> tag in <head>, using React 19's
 * native metadata hoisting. Supports the schemas required by rapport v2 §11:
 *   - Organization
 *   - WebSite
 *   - BreadcrumbList
 *   - ItemList
 *   - FAQPage
 *
 * Each helper below returns a plain JSON-serializable object; `<JsonLd>`
 * wraps it as a stringified <script>.
 */
const JsonLd = ({ data }) => {
  if (!data) return null;
  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
};

export const organizationSchema = (language = 'en') => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'MyGuardPod',
  url: CANONICAL_ORIGIN,
  logo: `${CANONICAL_ORIGIN}/images/LOGO_MGP.webp`,
  description: language === 'fr'
    ? 'MyGuardPod con\u00e7oit des \u00e9tuis protecteurs de transport pour des m\u00e9dicaments d\u2019urgence essentiels.'
    : 'MyGuardPod designs protective carrying cases for essential emergency medications.',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Baie-Comeau',
    addressRegion: 'Qu\u00e9bec',
    addressCountry: 'CA',
  },
  contactPoint: [{
    '@type': 'ContactPoint',
    email: 'contact@myguardpod.com',
    contactType: 'customer support',
    availableLanguage: ['fr-CA', 'en'],
  }],
  sameAs: [
    'https://www.etsy.com/shop/MyGuardPod',
  ],
});

export const websiteSchema = (language = 'en') => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'MyGuardPod',
  url: CANONICAL_ORIGIN,
  inLanguage: HREFLANG[language] || 'en',
});

/**
 * Generate a BreadcrumbList schema.
 *   crumbs: [{ name, routeKey }] — Home is auto-prepended.
 */
export const breadcrumbSchema = (crumbs, language = 'en') => {
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: language === 'fr' ? 'Accueil' : 'Home',
      item: getAbsoluteUrl('home', language),
    },
    ...crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 2,
      name: c.name,
      item: getAbsoluteUrl(c.routeKey, language),
    })),
  ];
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
};

/**
 * Generate an ItemList schema for the Project ACT product lineup.
 */
export const projectActItemListSchema = (language = 'en') => {
  const isFr = language === 'fr';
  const items = [
    {
      name: 'CortoPod ACT Mini',
      description: isFr
        ? 'Format ultra-compact pour fiole seule d\u2019hydrocortisone d\u2019urgence.'
        : 'Ultra-compact vial-only format for emergency hydrocortisone.',
      etsyUrl: 'https://www.etsy.com/shop/MyGuardPod',
    },
    {
      name: 'CortoPod ACT',
      description: isFr
        ? 'Format quotidien \u00e9quilibr\u00e9 pour kit d\u2019hydrocortisone injectable d\u2019urgence.'
        : 'Balanced everyday format for the emergency hydrocortisone injection kit.',
      etsyUrl: 'https://www.etsy.com/shop/MyGuardPod',
    },
    {
      name: 'CortoPod ACT Plus',
      description: isFr
        ? 'Format \u00e0 capacit\u00e9 \u00e9largie pour kit d\u2019hydrocortisone et fournitures suppl\u00e9mentaires.'
        : 'Expanded capacity format for the emergency hydrocortisone kit plus additional supplies.',
      etsyUrl: 'https://www.etsy.com/shop/MyGuardPod',
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Project ACT\u2122 lineup',
    numberOfItems: items.length,
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'Product',
        name: it.name,
        description: it.description,
        brand: { '@type': 'Brand', name: 'MyGuardPod' },
        url: it.etsyUrl,
      },
    })),
  };
};

/**
 * Generate a FAQPage schema from an array of Q/A pairs.
 */
export const faqSchema = (pairs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: pairs.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer,
    },
  })),
});

export default JsonLd;
